import { Box, Button, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type UseMutationResult, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthProvider";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { backendService } from "@/services/backend.service";
import { OTP_CODE_LENGTH, SigninLayout, useOtpCodeValidator } from "./common";
import { resolveSigninErrorMessage } from "./hooks/useSignInMutations";
import { useWebOtpAutofill } from "./hooks/useWebOtpAutofill";

function useVerifyOTP({ username, sub }: { username: string; sub: string }): UseMutationResult<void, Error, { code: string }, void> {
	const { saveTokenData } = useAuth();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();
	const navigate = useNavigate();

	return useMutation<void, Error, { code: string }, void>({
		mutationKey: ["useVerifyOTP", username, sub],
		mutationFn: async ({ code }) => {
			try {
				track("verify_otp", { status: "start" });
				const response = await backendService.verifyOTP({
					username,
					sub,
					code,
				});
				if (response.status >= 400) {
					throw Error(response.statusText);
				}

				const { isSuccessful, mfa } = await saveTokenData(response.data);
				if (!isSuccessful) {
					throw Error("Could not sign in");
				}

				track("verify_otp", { status: "success" });
				if (mfa) {
					navigate("/mfa", { state: { username, sub } });
				} else {
					navigate("/");
				}
			} catch (e) {
				console.error(e);

				let message = t("could.not.verify.one.time.password");
				if (e instanceof AxiosError) {
					message = resolveSigninErrorMessage(e, message, t);
				} else if (e instanceof Error) {
					message = e.message;
				}

				trackError("verify_otp", { message });
				showSnackbar(message, "error");
			}
		},
	});
}

function useResendOTP({ username }: { username: string }): UseMutationResult<void, Error, never, void> {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation<void, Error, never, void>({
		mutationKey: ["useResendOTP", username],
		mutationFn: async () => {
			try {
				track("resend-otp", { status: "start" });

				const response = await backendService.resendOTP(username);
				if (response.status >= 300) {
					throw Error("Could not verify code");
				}

				track("resend-otp", { status: "success" });
				showSnackbar(t("otp.resent.success", { username }), "success");
			} catch (e) {
				console.error(e);
				showSnackbar(t("otp.resent.error", { username }), "error");

				if (e instanceof Error) {
					trackError("verify_otp", { status: "fail", error: e.message });
				} else if (e instanceof AxiosError) {
					trackError("verify_otp", {
						status: "fail",
						error: e.response?.data?.detail,
					});
				}
			}
		},
	});
}

export const OneTimePassword: FC = () => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const { state } = useLocation();
	const sub = state?.sub as string | undefined;
	const username = state?.username as string | undefined;

	useEffect(() => {
		if (!sub || !username) {
			showSnackbar(t("error.sso.login.failed"), "error");
			navigate("/signin", { replace: true });
		}
	}, [sub, username, showSnackbar, t, navigate]);

	const { mutateAsync: verifyOTP, isPending: isVerifyOTPPending } = useVerifyOTP({ username: username ?? "", sub: sub ?? "" });
	const { mutateAsync: resendOTP, isPending: isResendOTPPending } = useResendOTP({ username: username ?? "" });
	const validateOtpCode = useOtpCodeValidator();

	// Drives the verify button's disabled state; the form still submits via FormData below.
	const [code, setCode] = useState("");

	const submitOTP = useCallback(
		async (formData: FormData) => {
			const code = formData.get("code")?.toString();

			if (!validateOtpCode(code)) {
				return;
			}

			try {
				await verifyOTP({ code: code as string });
			} catch (e) {
				console.error(e);
				showSnackbar(t("could.not.verify.one.time.password"), "error");
			}
		},
		[validateOtpCode, showSnackbar, verifyOTP, t],
	);

	const inputRef = useRef<HTMLInputElement>(null);
	// Separate ref to the underlying <input> for WebOTP autofill (Android); `ref` points at the MUI root.
	const codeInputRef = useRef<HTMLInputElement>(null);
	const formRef = useRef<HTMLFormElement>(null);
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const onOtpAutofilled = useCallback((autofilledCode: string) => {
		setCode(autofilledCode);
		if (autofilledCode.length === OTP_CODE_LENGTH) {
			formRef.current?.requestSubmit();
		}
	}, []);
	useWebOtpAutofill(codeInputRef, Boolean(sub && username), onOtpAutofilled);

	if (!sub || !username) return null;

	return (
		<SigninLayout title="Type Your Code">
			<Box component="form" ref={formRef} sx={{ display: "contents" }} action={submitOTP}>
				<TextField
					data-testid="otp-code-field"
					ref={inputRef}
					inputRef={codeInputRef}
					type="text"
					name="code"
					variant="outlined"
					size="small"
					placeholder="Verification code"
					value={code}
					onChange={(e) => setCode(e.target.value)}
					required
					slotProps={{
						// "one-time-code" lets iOS/Android browsers offer SMS OTP autofill (paired with the WebOTP line in the SMS).
						htmlInput: { maxLength: OTP_CODE_LENGTH, minLength: OTP_CODE_LENGTH, autoComplete: "one-time-code", inputMode: "numeric" },
					}}
				/>
				<Box sx={{ alignSelf: "center" }}>
					<Typography
						sx={{
							display: "inline-block",
							color: (theme) => alpha(theme.palette.primary.dark, 0.5),
							fontWeight: 400,
							fontSize: "13px",
							justifySelf: "center",
						}}
					>
						Didn't receive a code?
					</Typography>
					<Button
						data-testid="otp-resend-button"
						variant="text"
						size="small"
						sx={{
							display: "inline-block",
							px: "0.5rem",
							py: 0,
							width: "fit-content",
							fontWeight: 600,
							fontSize: "13px",
						}}
						onClick={resendOTP}
						loading={isResendOTPPending}
					>
						Send again
					</Button>
				</Box>

				<Button data-testid="otp-verify-button" type="submit" variant="contained" loading={isVerifyOTPPending} disabled={code.length !== OTP_CODE_LENGTH}>
					Verify and Proceed
				</Button>
			</Box>
		</SigninLayout>
	);
};
